import {
  CoachingFormData,
  STRUGGLE_OPTIONS,
  IMPACT_AREA_OPTIONS,
  ADHD_AWARENESS_OPTIONS,
} from './types';

interface Props {
  data: CoachingFormData;
  onChange: (updates: Partial<CoachingFormData>) => void;
  errors: Record<string, string>;
}

function CheckOption({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`text-left px-4 py-3 rounded-lg border text-sm transition-all duration-200 cursor-pointer ${
        checked
          ? 'border-[#1E2D5E] bg-[#1E2D5E]/5 text-[#1E2D5E] font-medium'
          : 'border-[#C9A96E]/30 bg-white text-[#1E2D5E]/70 hover:border-[#C9A96E]'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <div
          className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
            checked ? 'bg-[#1E2D5E] border-[#1E2D5E]' : 'border-[#C9A96E]/40'
          }`}
        >
          {checked && <i className="ri-check-line text-white text-xs" />}
        </div>
        <span>{label}</span>
      </div>
    </button>
  );
}

export default function IntakeStep2Struggles({ data, onChange, errors }: Props) {
  const toggle = (field: 'struggles' | 'life_impact_areas', value: string) => {
    const current = data[field] as string[];
    onChange({
      [field]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h3
          className="text-xl md:text-2xl font-light text-[#1E2D5E] mb-1"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Your Experience with ADHD
        </h3>
        <p className="text-sm text-[#1E2D5E]/60 leading-relaxed">
          There are no right or wrong answers — just share what feels true for you right now.
        </p>
      </div>

      {/* Current Struggles */}
      <div>
        <label className="block text-sm font-semibold text-[#1E2D5E] mb-1.5">
          Which of the following do you struggle with most right now?{' '}
          <span className="text-[#1E2D5E]/40 font-normal">(select all that apply)</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          {STRUGGLE_OPTIONS.map((opt) => (
            <CheckOption
              key={opt}
              label={opt}
              checked={data.struggles.includes(opt)}
              onToggle={() => toggle('struggles', opt)}
            />
          ))}
        </div>
        {data.struggles.includes('Other') && (
          <input
            type="text"
            name="struggles_other"
            value={data.struggles_other}
            onChange={(e) => onChange({ struggles_other: e.target.value })}
            placeholder="Tell us more..."
            className="mt-2 w-full px-4 py-3 rounded-lg border border-[#C9A96E]/30 text-[#1E2D5E] text-sm bg-white focus:outline-none focus:border-[#C9A96E] transition-colors"
          />
        )}
        {errors.struggles && <p className="text-xs text-red-400 mt-1">{errors.struggles}</p>}
      </div>

      {/* Awareness Duration */}
      <div>
        <label className="block text-sm font-semibold text-[#1E2D5E] mb-3">
          How long have you been aware that ADHD affects your daily life?
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {ADHD_AWARENESS_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange({ adhd_awareness: opt })}
              className={`px-3 py-3 rounded-lg border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                data.adhd_awareness === opt
                  ? 'border-[#1E2D5E] bg-[#1E2D5E] text-white'
                  : 'border-[#C9A96E]/30 bg-white text-[#1E2D5E]/70 hover:border-[#C9A96E]'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Life Impact Areas */}
      <div>
        <label className="block text-sm font-semibold text-[#1E2D5E] mb-1.5">
          In which areas of your life is ADHD having the biggest impact?{' '}
          <span className="text-[#1E2D5E]/40 font-normal">(select all that apply)</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          {IMPACT_AREA_OPTIONS.map((opt) => (
            <CheckOption
              key={opt}
              label={opt}
              checked={data.life_impact_areas.includes(opt)}
              onToggle={() => toggle('life_impact_areas', opt)}
            />
          ))}
        </div>
      </div>

      {/* Open-ended: Daily Life Description */}
      <div>
        <label className="block text-sm font-semibold text-[#1E2D5E] mb-1.5">
          In your own words, how is this currently affecting your daily life?{' '}
          <span className="text-red-400">*</span>
        </label>
        <p className="text-xs text-[#1E2D5E]/45 mb-2">
          Don&apos;t overthink it — just share what&apos;s been weighing on you most.
        </p>
        <textarea
          name="daily_life_description"
          value={data.daily_life_description}
          onChange={(e) => onChange({ daily_life_description: e.target.value })}
          placeholder="I've been feeling really overwhelmed lately because..."
          rows={4}
          maxLength={500}
          className={`w-full px-4 py-3 rounded-lg border text-[#1E2D5E] text-sm bg-white focus:outline-none transition-colors resize-none ${
            errors.daily_life_description ? 'border-red-400' : 'border-[#C9A96E]/30 focus:border-[#C9A96E]'
          }`}
        />
        <div className="flex justify-between mt-1">
          {errors.daily_life_description && (
            <p className="text-xs text-red-400">{errors.daily_life_description}</p>
          )}
          <p className="text-xs text-[#1E2D5E]/35 ml-auto">{data.daily_life_description.length}/500</p>
        </div>
      </div>
    </div>
  );
}
